// Banner del Parte de Montaje con firma presencial + envío portal
import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SignatureCanvas from "react-signature-canvas";
import {
  FileText, Loader2, Copy, MessageCircle, PenLine, Trash2, Download, Sparkles, X as IconX, CheckCircle2, Send,
} from "lucide-react";
import { toast } from "sonner";
import LearnerHint from "@/components/LearnerHint";

export function ParteMontajeBanner({ peticion, onChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openGenerar, setOpenGenerar] = useState(false);
  const [openFirmar, setOpenFirmar] = useState(false);
  const [openWA, setOpenWA] = useState(false);
  const [mensajeExtra, setMensajeExtra] = useState("");
  const [resultado, setResultado] = useState(null);

  const load = async () => {
    if (!peticion?.id) return;
    try {
      const { data } = await api.get(`/peticiones/${peticion.id}/parte-montaje/status`);
      setStatus(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [peticion?.id]);

  const generar = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/peticiones/${peticion.id}/parte-montaje/generar`, { mensaje_extra: mensajeExtra.trim() || null });
      setResultado(data);
      setOpenGenerar(false);
      setOpenWA(true);
      toast.success("Parte generado");
      await load();
      if (onChange) onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando parte");
    } finally { setLoading(false); }
  };

  const cancelar = async () => {
    if (!confirm("¿Cancelar este parte? Tendrás que generarlo de nuevo.")) return;
    try {
      await api.post(`/peticiones/${peticion.id}/parte-montaje/cancelar`);
      toast.success("Parte cancelado");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const copyMsg = () => {
    if (!resultado?.mensaje_whatsapp) return;
    navigator.clipboard.writeText(resultado.mensaje_whatsapp);
    toast.success("Copiado");
  };

  const openWhatsApp = () => {
    if (!resultado?.mensaje_whatsapp) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(resultado.mensaje_whatsapp)}`, "_blank");
  };

  if (!peticion?.id) return null;

  // Estado: firmado
  if (status?.estado === "firmado") {
    return (
      <div className="mb-6 rounded-xl p-4 border-2 border-emerald-500/40 bg-emerald-500/10" data-testid="banner-parte-firmado">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-emerald-200 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 fill-emerald-400/30" />
              Parte de montaje firmado
            </div>
            <div className="text-xs text-zinc-300 mt-1.5">
              Firmado por <b>{status.firmado_por}</b>
              {status.firmado_dni && <> · DNI {status.firmado_dni}</>}
              {status.firmado_at && <> · {status.firmado_at.slice(0, 10)}</>}
              {status.firmado_via && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/20 text-emerald-300">
                {status.firmado_via === "presencial" ? "📲 In-situ" : "🌐 Remoto"}
              </span>}
            </div>
          </div>
          {status.pdf_firmado_url && (
            <a href={status.pdf_firmado_url} target="_blank" rel="noreferrer" data-testid="btn-parte-pdf-firmado">
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold">
                <Download className="w-3 h-3 mr-1.5" /> PDF firmado
              </Button>
            </a>
          )}
        </div>
      </div>
    );
  }

  // Estado: pendiente firma
  if (status?.estado === "pendiente_firma") {
    return (
      <>
        <div className="mb-6 rounded-xl p-4 border-2 border-amber-500/40 bg-amber-500/10" data-testid="banner-parte-pendiente">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <div className="text-sm font-semibold text-amber-200 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Parte enviado · esperando firma del cliente
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={() => setOpenFirmar(true)} data-testid="btn-firmar-presencial"
                className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">
                <PenLine className="w-3 h-3 mr-1.5" /> Firmar in-situ
              </Button>
              {status.pdf_preview_url && (
                <a href={status.pdf_preview_url} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" data-testid="btn-parte-preview-pdf"><Download className="w-3 h-3 mr-1.5" /> PDF</Button>
                </a>
              )}
              <Button size="sm" variant="outline" onClick={() => { setResultado({ url: status.url, mensaje_whatsapp: null }); setOpenWA(true); }}
                data-testid="btn-parte-reenviar"
                className="bg-transparent border-amber-500/40 text-amber-200">
                <MessageCircle className="w-3 h-3 mr-1.5" /> Re-enviar
              </Button>
              <Button size="sm" variant="outline" onClick={cancelar} data-testid="btn-parte-cancelar"
                className="bg-transparent border-rose-500/40 text-rose-300">
                <Trash2 className="w-3 h-3 mr-1.5" /> Cancelar
              </Button>
            </div>
          </div>
          <div className="text-xs text-zinc-400 font-mono break-all flex items-center gap-2">
            {status.url}
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(status.url); toast.success("URL copiada"); }} className="h-6 px-2">
              <Copy className="w-3 h-3" />
            </Button>
          </div>
        </div>

        <FirmarPresencialModal
          open={openFirmar} onClose={() => setOpenFirmar(false)}
          peticionId={peticion.id}
          cliente={peticion.cliente_nombre || ""}
          onSigned={async () => { setOpenFirmar(false); await load(); }}
        />
        <WhatsAppModal open={openWA} onClose={() => setOpenWA(false)} resultado={resultado || { url: status.url, mensaje_whatsapp: null }} statusUrl={status.url} />
      </>
    );
  }

  // Sin generar — botón
  return (
    <>
      <div className="mb-6 rounded-xl p-4 border border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-orange-500/5 flex items-center justify-between gap-3 flex-wrap"
           data-testid="banner-generar-parte">
        <div className="text-sm">
          <span className="text-amber-200 font-semibold flex items-center gap-1.5">
            <FileText className="w-4 h-4" /> Parte de Montaje para Cliente <span className="text-amber-300/70 text-[10px] uppercase tracking-wider">(firma)</span>
          </span>
          <span className="text-zinc-400 text-xs mt-0.5 inline-block">Genera el parte y obtén la conformidad del cliente con firma digital (in-situ o por WhatsApp).</span>
        </div>
        <LearnerHint label="Generar parte de montaje firmable por el cliente">
          <Button size="sm" onClick={() => setOpenGenerar(true)} data-testid="btn-generar-parte"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">
            <Sparkles className="w-4 h-4 mr-1.5" /> Generar parte
          </Button>
        </LearnerHint>
      </div>

      <Dialog open={openGenerar} onOpenChange={(v) => !v && setOpenGenerar(false)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="modal-generar-parte">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2"><FileText className="w-5 h-5 text-amber-400" /> Generar parte de montaje</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-zinc-400">Crearemos un PDF con cabecera, líneas de trabajo, fotos aprobadas y zona para la firma del cliente. Te daremos un enlace que puedes mandar por WhatsApp o usar in-situ.</p>
            <div>
              <Label className="text-xs text-zinc-400">Mensaje adicional <span className="text-zinc-600">(opcional)</span></Label>
              <Textarea value={mensajeExtra} onChange={(e) => setMensajeExtra(e.target.value)} placeholder="Cualquier nota extra para el cliente..." rows={2} className="bg-zinc-900 border-zinc-700 mt-1" data-testid="input-mensaje-extra-parte" />
            </div>
            <Button onClick={generar} disabled={loading} className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold" data-testid="confirmar-generar-parte-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Generar PDF y enlace
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <WhatsAppModal open={openWA} onClose={() => setOpenWA(false)} resultado={resultado} />
    </>
  );
}

function FirmarPresencialModal({ open, onClose, peticionId, cliente, onSigned }) {
  const [nombre, setNombre] = useState("");
  const [dni, setDni] = useState("");
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);

  const submit = async () => {
    if (!nombre.trim()) { toast.error("Indica el nombre del firmante"); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error("El cliente debe firmar"); return; }
    setSaving(true);
    try {
      const firmaBase64 = sigRef.current.getTrimmedCanvas().toDataURL("image/png");
      await api.post(`/peticiones/${peticionId}/parte-montaje/firmar-presencial`, {
        firma_base64: firmaBase64,
        nombre: nombre.trim(),
        dni: dni.trim() || null,
      });
      toast.success("Parte firmado correctamente");
      onSigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando firma");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="modal-firmar-presencial">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2"><PenLine className="w-5 h-5 text-amber-400" /> Firma in-situ del cliente</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-zinc-400">Pasa tu móvil al cliente para que firme. {cliente && <span>Cliente: <b className="text-zinc-200">{cliente}</b></span>}</p>
          <div>
            <Label className="text-xs text-zinc-400">Nombre del firmante *</Label>
            <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej: María Pérez (encargada)" className="bg-zinc-900 border-zinc-700 mt-1" data-testid="input-firmante-nombre" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">DNI/NIF <span className="text-zinc-600">(opcional)</span></Label>
            <Input value={dni} onChange={(e) => setDni(e.target.value)} placeholder="12345678X" className="bg-zinc-900 border-zinc-700 mt-1 font-mono" data-testid="input-firmante-dni" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs text-zinc-400">Firma del cliente *</Label>
              <Button variant="ghost" size="sm" onClick={() => sigRef.current?.clear()} className="text-xs h-7" data-testid="btn-presencial-limpiar">
                <Trash2 className="w-3 h-3 mr-1" /> Borrar
              </Button>
            </div>
            <div className="bg-white rounded-lg overflow-hidden border-2 border-amber-500/40 touch-none">
              <SignatureCanvas
                ref={sigRef}
                canvasProps={{ className: "w-full h-48", "data-testid": "presencial-canvas" }}
                penColor="#1f2937"
                minWidth={1.5}
                maxWidth={3}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={onClose} variant="outline" className="flex-1">Cancelar</Button>
            <Button onClick={submit} disabled={saving} data-testid="btn-firmar-presencial-confirm"
              className="flex-1 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PenLine className="w-4 h-4 mr-2" />}
              Confirmar firma
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WhatsAppModal({ open, onClose, resultado, statusUrl }) {
  if (!resultado) return null;
  const msg = resultado.mensaje_whatsapp || `¡Hola! Adjuntamos el Parte de Montaje para tu conformidad:\n${resultado.url || statusUrl}\n\nGracias.\nRotulaciónDNF`;
  const copy = () => { navigator.clipboard.writeText(msg); toast.success("Copiado"); };
  const openWA = () => { window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank"); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="modal-parte-whatsapp">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-emerald-400" /> Enviar al cliente</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Textarea value={msg} readOnly rows={7} className="bg-zinc-900 border-zinc-700 text-xs font-mono" data-testid="parte-wa-textarea" />
          <div className="flex gap-2">
            <Button onClick={copy} variant="outline" className="flex-1"><Copy className="w-3 h-3 mr-1" /> Copiar</Button>
            <Button onClick={openWA} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold" data-testid="btn-parte-wa-open">
              <Send className="w-3 h-3 mr-1" /> Abrir WhatsApp
            </Button>
          </div>
          <Button onClick={onClose} variant="ghost" className="w-full text-zinc-400"><IconX className="w-3 h-3 mr-1" /> Cerrar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
